import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { CheckCircle, XCircle, Ticket } from 'lucide-react';
import api from '../../lib/api';

const paymentMethods = ['cash', 'card', 'bank_transfer', 'upi'];
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n || 0);

export const PaymentFormDialog = ({ open, onOpenChange, onSubmit, formData, setFormData, bookings }) => {
  const [couponStatus, setCouponStatus] = useState(null); // null | {valid, discount, message}
  const [validating, setValidating] = useState(false);

  const validateCoupon = async () => {
    const code = formData.coupon_code?.trim();
    if (!code) { setCouponStatus(null); return; }
    setValidating(true);
    try {
      const res = await api.post(`/coupons/validate?code=${code}&amount=${parseFloat(formData.amount) || 0}`);
      setCouponStatus({ valid: true, discount: res.data.discount, final: res.data.final_amount, message: `${code}: -${fmt(res.data.discount)}` });
    } catch (err) {
      setCouponStatus({ valid: false, discount: 0, message: err.response?.data?.detail || 'Invalid coupon' });
    }
    setValidating(false);
  };

  const handleClose = (v) => {
    setCouponStatus(null);
    onOpenChange(v);
  };

  const amount = parseFloat(formData.amount) || 0;
  const discount = couponStatus?.valid ? couponStatus.discount : 0;
  const finalAmount = Math.max(amount - discount, 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Record Payment</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Booking</Label>
            <Select value={formData.booking_id} onValueChange={(value) => setFormData({...formData, booking_id: value})}>
              <SelectTrigger className="border-stone-300" data-testid="select-booking"><SelectValue placeholder="Select a booking" /></SelectTrigger>
              <SelectContent className="bg-white">
                {bookings.map(booking => (
                  <SelectItem key={booking.id} value={booking.id}>
                    {booking.guest?.name} - Room {booking.room?.room_number} ({fmt(booking.total_amount)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Amount (₹)</Label>
            <Input id="amount" type="number" min="0" step="0.01" value={formData.amount} onChange={(e) => { setFormData({...formData, amount: e.target.value}); setCouponStatus(null); }} placeholder="0.00" required className="border-stone-300" data-testid="payment-amount-input" />
          </div>

          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select value={formData.payment_method} onValueChange={(value) => setFormData({...formData, payment_method: value})}>
              <SelectTrigger className="border-stone-300" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {paymentMethods.map(method => (<SelectItem key={method} value={method} className="capitalize">{method === 'upi' ? 'UPI' : method.replace('_', ' ')}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          {/* Coupon */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><Ticket className="w-3.5 h-3.5 text-sage-600" />Coupon Code (optional)</Label>
            <div className="flex gap-2">
              <Input value={formData.coupon_code || ''} onChange={(e) => { setFormData({...formData, coupon_code: e.target.value.toUpperCase()}); setCouponStatus(null); }} placeholder="e.g. WELCOME20" className="border-stone-300 font-mono flex-1" data-testid="payment-coupon-input" />
              <Button type="button" variant="outline" onClick={validateCoupon} disabled={!formData.coupon_code || validating} className="border-stone-300 px-4" data-testid="validate-coupon-btn">
                {validating ? '...' : 'Apply'}
              </Button>
            </div>
            {couponStatus && (
              <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md ${couponStatus.valid ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`} data-testid="coupon-status">
                {couponStatus.valid ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {couponStatus.message}
              </div>
            )}
          </div>

          {/* Payment Summary */}
          {amount > 0 && (
            <div className="bg-stone-50 rounded-md p-3 space-y-1" data-testid="payment-summary">
              <div className="flex justify-between text-sm text-stone-600">
                <span>Amount</span><span>{fmt(amount)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm text-emerald-600">
                  <span>Discount ({formData.coupon_code})</span><span>-{fmt(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold text-stone-900 pt-1 border-t border-stone-200">
                <span>Final Amount</span><span>{fmt(finalAmount)}</span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Optional notes..." className="border-stone-300 min-h-[60px]" data-testid="payment-notes-input" />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => handleClose(false)} className="border-stone-300">Cancel</Button>
            <Button type="submit" className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="submit-payment-button">
              Record {fmt(finalAmount)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
